import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import { useDialogs } from "../context/DialogContext";
import usePermiso from "../hooks/usePermiso";
import api from "../services/api";
import ModalDetallePropiedad, {
  BadgePosibleAgencia,
  BadgeTelefonoPendienteIdealista,
  BadgePrecaptacionAna,
  BadgePipelineUsuario,
} from "../components/ModalDetallePropiedad";
import ModalWhatsApp from "../components/ModalWhatsApp";
import ModalEmail from "../components/ModalEmail";
import ModalNota from "../components/ModalNota";
import ModalAlertasMarketplace from "../components/ModalAlertasMarketplace";
import ModalImprimirPropiedades from "../components/propiedades/ModalImprimirPropiedades";
import PanelCoincidenciasMercado from "../components/PanelCoincidenciasMercado";
import { llamarContacto } from "../utils/gestionVentasContactos";
import { useCaptacionRelacionContacto } from "../hooks/useCaptacionRelacionContacto";
import ModalRelacionContactoExistente from "../components/ModalRelacionContactoExistente";

const IMG_PROXY_BASE = window.location.hostname === "localhost"
  ? "http://localhost:4000/api/img-proxy?url="
  : "https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=";
const proxyFoto = (url) => (url ? IMG_PROXY_BASE + encodeURIComponent(url) : url);


const PORTAL_CFG = {
  fotocasa:    { label: "F",  name: "Fotocasa",    color: "#e97316" },
  idealista:   { label: "I",  name: "Idealista",   color: "#2563eb" },
  pisos:       { label: "P",  name: "Pisos.com",   color: "#7c3aed" },
  habitaclia:  { label: "H",  name: "Habitaclia",  color: "#0891b2" },
  kyero:       { label: "K",  name: "Kyero",       color: "#16a34a" },
  milanuncios: { label: "M",  name: "Milanuncios", color: "#dc2626" },
  thinkspain:  { label: "T",  name: "ThinkSpain",  color: "#1e3a5f" },
  todopisos:   { label: "TP", name: "Todopisos",   color: "#0e7490" },
};

const POBLS_PRIN  = ["Dénia","La Xara","El Verger","Els Poblets","Ondara","Beniarbeig","Pedreguer","Pego","Oliva"];
const POBLS_MAS   = ["Jávea/Xàbia","Gata de Gorgos","Calpe/Calp","Teulada","Benissa","Altea","Benidorm","Jalón/Xaló","La Nucia","Llíber","Orba","Parcent","Murla","Benidoleig"];
const TIPOS_INM   = ["Piso","Apartamento","Chalet","Villa","Casa","Ático","Dúplex","Finca","Terreno","Local"];
const ORIENTACS   = ["Norte","Sur","Este","Oeste","Nordeste","Noroeste","Sudeste","Suroeste"];
const ORDER_OPTS  = [
  { v: "created_at_desc", l: "Más recientes" },
  { v: "precio_asc",      l: "Precio ↑" },
  { v: "precio_desc",     l: "Precio ↓" },
];

const MERCADO_GREEN = '#22c55e';

function fmtConteoMercado(viviendas, anuncios) {
  const a = Number(anuncios) || 0;
  const v = Number(viviendas) || 0;
  return `${v.toLocaleString("es-ES")} viviendas en ${a.toLocaleString("es-ES")} anuncios`;
}
const EXTRAS_LIST = [
  ["piscina",      "🏊 Piscina"],
  ["garaje",       "🚗 Garaje"],
  ["terraza",      "🌿 Terraza"],
  ["ascensor",     "🛗 Ascensor"],
  ["amueblado",    "🪑 Amueblado"],
  ["primera_linea","🏖️ 1ª línea"],
  ["vista_mar",    "🌊 Vista mar"],
  ["pista_padel",  "🎾 Pádel"],
];

const CRITERIO_DBL_MS = 380;

const FILTROS_INIT = {
  portales: [], portalesExcl: [],
  tipo_anunciante: "", tipo_anuncianteExcl: "",
  operacion: "", operacionExcl: "",
  tipo_inmueble: "", tipo_inmuebleExcl: "",
  pobls: [], poblsExcl: [],
  precio_min: "", precio_max: "",
  hab_min: "", hab_minExcl: "",
  banos_min: "", banos_minExcl: "",
  m2_min: "", m2_max: "",
  parcela_min: "", orientacion: "", orientacionExcl: "",
  piscina: false, garaje: false, terraza: false,
  ascensor: false, amueblado: false,
  primera_linea: false, vista_mar: false, pista_padel: false,
  piscinaExcl: false, garajeExcl: false, terrazaExcl: false,
  ascensorExcl: false, amuebladoExcl: false,
  primera_lineaExcl: false, vista_marExcl: false, pista_padelExcl: false,
  sin_vitalcasa: false,
};

// ── Chip idéntico al de FichaCliente ─────────────────────────────────────────
function Chip({ label, active, onClick, small, theme, excluded }) {
  const c = theme.colors;
  const ex = excluded === true;
  const exBg = theme.name === "dark" ? "#991b1b" : "#dc2626";
  const border = ex ? "transparent" : active ? c.chipActiveBorder : c.chipBorder;
  const background = ex ? exBg : active ? c.chipActiveBg : c.chipBg;
  const color = ex ? "#ffffff" : active ? c.chipActiveText : c.chipText;
  return (
    <button type="button" onClick={onClick} style={{
      padding: small ? "2px 8px" : "4px 10px",
      fontSize: small ? 10 : 11, fontWeight: 500, borderRadius: 6,
      border: `1px solid ${border}`,
      background, color,
      cursor: "pointer", transition: "all 0.15s", userSelect: "none", lineHeight: "1.4",
      fontFamily: "inherit",
    }}
      onMouseEnter={e => {
        if (ex || active) return;
        e.target.style.background = c.chipHoverBg;
        e.target.style.color = c.chipHoverText;
        e.target.style.borderColor = c.chipHoverBorder;
      }}
      onMouseLeave={e => {
        if (ex || active) return;
        e.target.style.background = c.chipBg;
        e.target.style.color = c.chipText;
        e.target.style.borderColor = c.chipBorder;
      }}
    >{label}</button>
  );
}

function PortalFilterChip({ portalKey, included, excluded, onClick, theme, compact }) {
  const c = theme.colors;
  const cfg = PORTAL_CFG[portalKey];
  if (!cfg) return null;
  const ex = excluded === true;
  const active = included === true;
  const exBg = theme.name === "dark" ? "#991b1b" : "#dc2626";
  const border = ex ? "transparent" : active ? c.chipActiveBorder : c.chipBorder;
  const background = ex ? exBg : active ? c.chipActiveBg : (compact ? "transparent" : c.chipBg);
  const color = ex ? "#ffffff" : active ? c.chipActiveText : c.chipText;
  return (
    <button type="button" onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 4,
      padding: compact ? "3px 8px" : "4px 10px",
      fontSize: compact ? 10 : 11, fontWeight: 600, borderRadius: 6,
      border: `1px solid ${border}`,
      background, color,
      cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
    }}>
      <PortalBadge portal={portalKey} size={compact ? 16 : 14} />
      {cfg.name}
    </button>
  );
}

function PortalBadge({ portal, size = 20 }) {
  const cfg = PORTAL_CFG[portal];
  if (!cfg) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: 4, flexShrink: 0,
      background: cfg.color, color: "#fff",
      fontSize: size <= 18 ? 8 : 9, fontWeight: 800,
    }}>{cfg.label}</span>
  );
}

function PortalLabel({ portal }) {
  const cfg = PORTAL_CFG[portal];
  const name = cfg?.name || (portal ? String(portal).charAt(0).toUpperCase() + String(portal).slice(1) : null);
  if (!name) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 6,
      background: cfg?.color || "#475569", color: "#fff",
      fontSize: 10, fontWeight: 700, lineHeight: 1.2,
      boxShadow: "0 1px 4px rgba(0,0,0,0.35)", flexShrink: 0,
    }}>
      {cfg && <PortalBadge portal={portal} size={14} />}
      {name}
    </span>
  );
}

export default function ScrapingPortales() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const [searchParams] = useSearchParams();
  const verFiltroPoblaciones = puede("scr_filtro_poblaciones", "ver");
  const verFiltroTipoInm     = puede("scr_filtro_tipo_inm", "ver");
  const verFiltroOrientacion = puede("scr_filtro_orientacion", "ver");
  const verFiltroOrden       = puede("scr_filtro_orden", "ver");
  const verFiltroExtras      = puede("scr_filtro_extras", "ver");
  const verPortal            = useCallback((key) => puede(`scr_portal_${key}`, "ver"), [puede]);
  const verGrupos            = puede("scr_grupos_duplicados", "ver");
  const verRevisar           = puede("scr_revisar", "ver");
  const modRevisar           = puede("scr_revisar", "modificar");
  const verAlertas           = puede("scr_alertas", "ver");

  const [vista, setVista]       = useState("listado"); // listado | grupos | revision
  const [filtros, setFiltros]   = useState(FILTROS_INIT);
  const [modalAlertas, setModalAlertas] = useState(false);
  const [order, setOrder]       = useState("created_at_desc");
  const [page, setPage]         = useState(1);
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [viviendas, setViviendas] = useState(0);
  const [portalCounts, setPortalCounts] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [revisionMsg, setRevisionMsg] = useState(null);
  const [resolvingRevisionId, setResolvingRevisionId] = useState(null);
  const [stats, setStats]       = useState(null);
  const [mercadoStats, setMercadoStats] = useState(null);
  const [grupos, setGrupos]     = useState([]);
  const [gruposTotal, setGruposTotal] = useState(0);
  const [gruposPage, setGruposPage] = useState(1);
  const [revision, setRevision] = useState([]);
  const [revisionTotal, setRevisionTotal] = useState(0);
  const [matchRunning, setMatchRunning] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [propActiva, setPropActiva] = useState(null);
  const [masPobls, setMasPobls] = useState(false);
  const [modalProp, setModalProp] = useState(null);
  const [vincularBusy, setVincularBusy] = useState(false);
  const [vincularMsg, setVincularMsg] = useState(null);
  const [modoVincularPanel, setModoVincularPanel] = useState(false);
  const abortRef = useRef(null);
  const deepLinkDoneRef = useRef(null);
  const criterioChipDblRef = useRef({ id: null, t: 0, timer: null });
  const LIMIT = 40;

  // Deep-link alertas WA: /scraping-portales?id=<uuid scraping_resultados>
  useEffect(() => {
    const deepId = String(searchParams.get("id") || searchParams.get("ad") || "").trim();
    if (!deepId || deepLinkDoneRef.current === deepId) return;
    deepLinkDoneRef.current = deepId;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/scraping/propiedades/${deepId}`);
        if (cancelled || !data?.id) return;
        setModalProp(data);
        setPropActiva(data);
        setVista("listado");
      } catch (e) {
        console.warn("[ScrapingPortales] deep-link id=", deepId, e?.response?.status || e.message);
        deepLinkDoneRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const scheduleCriterioInclExcl = useCallback((id, onSingle, onDouble) => {
    const now = Date.now();
    const r = criterioChipDblRef.current;
    if (r.id === id && now - r.t < CRITERIO_DBL_MS) {
      if (r.timer) { clearTimeout(r.timer); r.timer = null; }
      r.id = null;
      r.t = 0;
      onDouble();
      return;
    }
    r.id = id;
    r.t = now;
    if (r.timer) clearTimeout(r.timer);
    r.timer = setTimeout(() => {
      r.timer = null;
      r.id = null;
      onSingle();
    }, CRITERIO_DBL_MS);
  }, []);

  const onArrayInclExclChip = useCallback((inclKey, exclKey, val) => {
    scheduleCriterioInclExcl(
      `${inclKey}:${val}`,
      () => setFiltros((p) => {
        const inc = p[inclKey] || [];
        const ex = p[exclKey] || [];
        if (inc.includes(val)) return { ...p, [inclKey]: inc.filter((x) => x !== val), [exclKey]: ex.filter((x) => x !== val) };
        return { ...p, [inclKey]: [...inc, val], [exclKey]: ex.filter((x) => x !== val) };
      }),
      () => setFiltros((p) => {
        const ex = p[exclKey] || [];
        const inc = p[inclKey] || [];
        if (ex.includes(val)) return { ...p, [exclKey]: ex.filter((x) => x !== val) };
        return { ...p, [inclKey]: inc.filter((x) => x !== val), [exclKey]: [...ex, val] };
      }),
    );
  }, [scheduleCriterioInclExcl]);

  const onSingleInclExclChip = useCallback((inclKey, exclKey, val) => {
    scheduleCriterioInclExcl(
      `${inclKey}:${val}`,
      () => setFiltros((p) => {
        if (p[inclKey] === val) return { ...p, [inclKey]: "", [exclKey]: "" };
        return { ...p, [inclKey]: val, [exclKey]: "" };
      }),
      () => setFiltros((p) => {
        if (p[exclKey] === val) return { ...p, [inclKey]: "", [exclKey]: "" };
        return { ...p, [inclKey]: "", [exclKey]: val };
      }),
    );
  }, [scheduleCriterioInclExcl]);

  const onBoolTriChip = useCallback((inclField, exclField) => {
    scheduleCriterioInclExcl(
      `bool:${inclField}`,
      () => setFiltros((p) => {
        if (p[inclField]) return { ...p, [inclField]: false, [exclField]: false };
        return { ...p, [inclField]: true, [exclField]: false };
      }),
      () => setFiltros((p) => {
        if (p[exclField]) return { ...p, [inclField]: false, [exclField]: false };
        return { ...p, [inclField]: false, [exclField]: true };
      }),
    );
  }, [scheduleCriterioInclExcl]);

  const loadMercadoStats = useCallback(() => {
    api.get("/scraping/mercado/stats").then(r => setMercadoStats(r.data)).catch(() => {});
  }, []);

  const loadStats = useCallback(() => {
    const params = {};
    if (filtros.pobls?.length) params.poblacion = filtros.pobls.join(",");
    api.get("/scraping/stats", { params }).then(r => setStats(r.data)).catch(() => {});
  }, [filtros.pobls]);

  // Cargar estadísticas de portales (respeta filtro de población)
  useEffect(() => {
    loadStats();
    loadMercadoStats();
  }, [loadStats, loadMercadoStats]);

  const cargar = useCallback(async (f, pg, ord) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pg, limit: LIMIT, order: ord };
      if (f.portales?.length)     params.portales = f.portales.join(",");
      if (f.portalesExcl?.length) params.portal_excl = f.portalesExcl.join(",");
      if (f.tipo_anunciante)      params.tipo_anunciante = f.tipo_anunciante;
      if (f.tipo_anuncianteExcl)  params.tipo_anunciante_excl = f.tipo_anuncianteExcl;
      if (f.operacion)            params.operacion = f.operacion;
      if (f.operacionExcl)        params.operacion_excl = f.operacionExcl;
      if (f.tipo_inmueble)        params.tipo_inmueble = f.tipo_inmueble;
      if (f.tipo_inmuebleExcl)    params.tipo_inmueble_excl = f.tipo_inmuebleExcl;
      if (f.pobls?.length)        params.poblacion = f.pobls.join(",");
      if (f.poblsExcl?.length)    params.poblacion_excl = f.poblsExcl.join(",");
      if (f.precio_min)           params.precio_min = f.precio_min;
      if (f.precio_max)           params.precio_max = f.precio_max;
      if (f.hab_min && f.hab_min !== "")    params.hab_min = f.hab_min.replace("+", "");
      if (f.hab_minExcl && f.hab_minExcl !== "") params.hab_min_excl = f.hab_minExcl.replace("+", "");
      if (f.banos_min && f.banos_min !== "") params.banos_min = f.banos_min.replace("+", "");
      if (f.banos_minExcl && f.banos_minExcl !== "") params.banos_min_excl = f.banos_minExcl.replace("+", "");
      if (f.m2_min)               params.m2_min = f.m2_min;
      if (f.m2_max)               params.m2_max = f.m2_max;
      if (f.parcela_min)          params.parcela_min = f.parcela_min;
      if (f.orientacion)          params.orientacion = f.orientacion;
      if (f.orientacionExcl)      params.orientacion_excl = f.orientacionExcl;
      EXTRAS_LIST.forEach(([k]) => {
        if (f[k]) params[k] = "true";
        if (f[`${k}Excl`]) params[`${k}_excl`] = "true";
      });
      if (f.sin_vitalcasa) params.sin_vitalcasa = "1";

      const { data } = await api.get("/scraping/propiedades", { params });
      setTotal(data.total ?? 0);
      setViviendas(data.viviendas ?? 0);
      setPortalCounts(Array.isArray(data.portales) ? data.portales : null);
      setItems(data.items || []);
    } catch (e) {
      console.error("[ScrapingPortales]", e);
      const msg = e.response?.data?.error || e.message || "Error desconocido";
      setError(msg);
      setTotal(0);
      setViviendas(0);
      setPortalCounts(null);
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (vista !== "listado") return;
    setPage(1);
    setSeleccionadas([]);
    cargar(filtros, 1, order);
  }, [filtros, order, cargar, vista]);

  const cargarGrupos = useCallback(async (pg = 1) => {
    setLoading(true);
    setError(null);
    try {
      const municipio = filtros.pobls?.length === 1
        ? filtros.pobls[0]
        : (filtros.pobls?.includes("Gata de Gorgos") ? "Gata de Gorgos" : (filtros.pobls?.includes("Ondara") ? "Ondara" : undefined));
      const { data } = await api.get("/scraping/mercado/grupos", {
        params: { page: pg, limit: LIMIT, min_portales: 2, municipio },
      });
      setGrupos(data.items || []);
      setGruposTotal(data.total || 0);
      setGruposPage(pg);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setGrupos([]);
      setGruposTotal(0);
    } finally { setLoading(false); }
  }, [filtros.pobls]);

  const cargarRevision = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/scraping/mercado/revision", { params: { estado: "pendiente", limit: 100 } });
      setRevision(data.items || []);
      setRevisionTotal(data.total || 0);
      loadMercadoStats();
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setError(msg);
      setRevisionMsg(msg);
      setRevision([]);
      setRevisionTotal(0);
    } finally { setLoading(false); }
  }, [loadMercadoStats]);

  useEffect(() => {
    if (vista === "grupos") cargarGrupos(1);
    if (vista === "revision") cargarRevision();
  }, [vista, cargarGrupos, cargarRevision]);

  const ejecutarMatching = async () => {
    setMatchRunning(true);
    try {
      const municipio = filtros.pobls?.length === 1
        ? filtros.pobls[0]
        : (filtros.pobls?.includes("Ondara") ? "Ondara" : (filtros.pobls?.includes("Gata de Gorgos") ? "Gata de Gorgos" : (filtros.pobls?.[0] || "Gata de Gorgos")));
      await api.post("/scraping/mercado/ejecutar", { municipio, reset: false });
      loadStats();
      loadMercadoStats();
      if (vista === "grupos") cargarGrupos(1);
      else if (vista === "revision") cargarRevision();
      else cargar(filtros, page, order);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setMatchRunning(false); }
  };

  const resolverRevision = async (id, accion, scrapingIds = null) => {
    setResolvingRevisionId(id);
    setRevisionMsg(null);
    setError(null);
    try {
      if (accion === "confirmar") {
        await api.post(`/scraping/mercado/revision/${id}/confirmar`, {
          scraping_ids: scrapingIds,
        });
        setRevisionMsg("Vinculación guardada.");
      } else {
        await api.post(`/scraping/mercado/revision/${id}/rechazar`);
        setRevisionMsg("Grupo descartado.");
      }
      setRevision((prev) => prev.filter((r) => r.id !== id));
      setRevisionTotal((n) => Math.max(0, n - 1));
      loadMercadoStats();
      cargarRevision();
      if (vista === "grupos") cargarGrupos(gruposPage);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || "No se pudo guardar";
      setError(msg);
      setRevisionMsg(msg);
    } finally {
      setResolvingRevisionId(null);
    }
  };

  const setF = (k, v) => setFiltros((prev) => ({ ...prev, [k]: v }));
  const resetFiltros = () => {
    setFiltros({ ...FILTROS_INIT });
    setSeleccionadas([]);
    setPropActiva(null);
    setModoVincularPanel(false);
  };
  const limpiarPortales = () => setFiltros((prev) => ({ ...prev, portales: [], portalesExcl: [] }));

  const propiedadId = (p) => p?.id ?? p?.id_propiedad_mercado;

  const idsOrigenVincular = useMemo(() => {
    if (!modoVincularPanel) return [];
    if (seleccionadas.length) {
      return seleccionadas.map((sid) => {
        const item = items.find((p) => String(propiedadId(p)) === String(sid));
        return item?.id || sid;
      }).filter(Boolean);
    }
    if (propActiva?.id) return [propActiva.id];
    return [];
  }, [modoVincularPanel, seleccionadas, items, propActiva?.id]);

  const showPanelCoincidencias = vista === "listado" && modoVincularPanel && idsOrigenVincular.length >= 1;

  /** IDs reales de scraping_resultados de todo lo marcado con checkbox (para "Enviar a mi Pipeline" en lote). */
  const idsSeleccionadosPipeline = useMemo(() => {
    if (seleccionadas.length) {
      return seleccionadas.map((sid) => {
        const item = items.find((p) => String(propiedadId(p)) === String(sid));
        return item?.id || sid;
      }).filter(Boolean);
    }
    if (propActiva?.id) return [propActiva.id];
    return [];
  }, [seleccionadas, items, propActiva?.id]);

  const abrirModoVincular = () => {
    if (seleccionadas.length < 1) return;
    setModoVincularPanel(true);
    setVincularMsg(null);
  };

  const cerrarModoVincular = () => {
    setModoVincularPanel(false);
    setVincularMsg(null);
  };

  const crearGrupoSeleccion = async () => {
    if (idsOrigenVincular.length < 2) return;
    setVincularBusy(true);
    setVincularMsg(null);
    try {
      await api.post("/scraping/mercado/crear-grupo", { scraping_ids: idsOrigenVincular });
      setVincularMsg("Grupo creado correctamente.");
      setSeleccionadas([]);
      setPropActiva(null);
      setModoVincularPanel(false);
      loadMercadoStats();
      cargar(filtros, page, order);
    } catch (e) {
      setVincularMsg(e.response?.data?.error || e.message || "Error al crear grupo");
    } finally {
      setVincularBusy(false);
    }
  };

  const refreshTrasVincular = useCallback(() => {
    loadMercadoStats();
    cargar(filtros, page, order);
    setVincularMsg("Vinculación guardada.");
  }, [loadMercadoStats, cargar, filtros, page, order]);

  const toggleSel = (p) => {
    const id = propiedadId(p);
    if (!id) return;
    setSeleccionadas((prev) => {
      const ya = prev.includes(id);
      if (ya) {
        setPropActiva((cur) => (propiedadId(cur) === id ? null : cur));
        return prev.filter((x) => x !== id);
      }
      setPropActiva(p);
      return [...prev, id];
    });
  };

  const seleccionarProp = (p) => {
    setPropActiva(p);
    const id = propiedadId(p);
    if (id && !seleccionadas.includes(id)) {
      setSeleccionadas((prev) => [...prev, id]);
    }
  };

  useEffect(() => {
    if (vista !== "listado") setModoVincularPanel(false);
  }, [vista]);

  useEffect(() => {
    if (modoVincularPanel && seleccionadas.length < 1 && !propActiva?.id) {
      setModoVincularPanel(false);
    }
  }, [modoVincularPanel, seleccionadas.length, propActiva?.id]);

  const totalPages = Math.ceil((viviendas || total) / LIMIT);
  const anunciosCount = vista === "listado" ? total : (mercadoStats?.anuncios ?? 0);
  const viviendasCount = vista === "listado" ? viviendas : (mercadoStats?.viviendas ?? 0);
  const conteoMercado = fmtConteoMercado(viviendasCount, anunciosCount);
  const portalCountMap = Object.fromEntries(
    (portalCounts ?? stats?.portales ?? []).map((p) => [p.portal, p.total]),
  );

  // Estilos panel izquierdo — idénticos a FichaCliente
  const panelBg     = isDark ? "#0e1117" : "#f1f5fb";
  const sectionLabel = {
    fontSize: 13, fontWeight: 600,
    color: isDark ? c.textPrimary : "#1d4ed8",
    marginBottom: 4,
  };
  const sInput = {
    width: "100%", height: 28, padding: "0 8px",
    background: c.bgInput, border: `1px solid ${c.border}`,
    borderRadius: 6, color: c.textPrimary, fontSize: 11, fontFamily: "inherit",
    outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 48px)", overflow: "hidden",
      background: panelBg,
    }}>

      {/* ── TOPBAR: 50% filtros | 50% resultados ───────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "stretch",
        background: isDark ? "#0a0c12" : "#dce3ed",
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
        flexShrink: 0,
      }}>
        {/* Mitad izquierda — título, portales, limpiar */}
        <div style={{
          width: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", gap: 8,
          padding: "5px 14px", flexWrap: "wrap",
          borderRight: `1px solid ${c.sectionBorder}`,
          boxSizing: "border-box",
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, marginRight: 4, flexShrink: 0 }}>
            🕷️ MarketPlace
          </span>

          {stats?.portales?.map(p => {
            const cfg = PORTAL_CFG[p.portal];
            if (!cfg || !verPortal(p.portal)) return null;
            const incluido = filtros.portales.includes(p.portal);
            const excluido = filtros.portalesExcl.includes(p.portal);
            const badgeTotal = portalCountMap[p.portal] ?? 0;
            return (
              <span key={p.portal} style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                <PortalFilterChip
                  portalKey={p.portal}
                  included={incluido}
                  excluded={excluido}
                  compact
                  theme={theme}
                  onClick={() => onArrayInclExclChip("portales", "portalesExcl", p.portal)}
                />
                <span style={{
                  fontSize: 10, color: "#fff", fontWeight: 700,
                  background: cfg.color, padding: "0 5px", borderRadius: 10, lineHeight: "16px",
                }}>{badgeTotal}</span>
              </span>
            );
          })}

          <div style={{ flex: 1, minWidth: 8 }} />

          {verAlertas && (
            <button
              type="button"
              onClick={() => setModalAlertas(true)}
              title="Crear y gestionar alertas con los filtros actuales"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                border: `1px solid ${isDark ? "rgba(59,130,246,0.45)" : "rgba(37,99,235,0.35)"}`,
                background: isDark ? "rgba(37,99,235,0.25)" : "rgba(37,99,235,0.1)",
                color: isDark ? "#93c5fd" : "#1d4ed8",
                cursor: "pointer", fontFamily: "inherit",
                flexShrink: 0, lineHeight: 1.2,
              }}
            >🔔 Alertas</button>
          )}

          <button
            type="button"
            onClick={resetFiltros}
            title="Limpiar todos los filtros"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
              border: "1px solid transparent",
              background: isDark ? "#991b1b" : "#dc2626",
              color: "#fff", cursor: "pointer", fontFamily: "inherit",
              flexShrink: 0, lineHeight: 1.2,
            }}
          >✕ Limpiar</button>
        </div>

        {/* Mitad derecha — pestañas, matching, conteo, orden */}
        <div style={{
          width: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", gap: 8,
          padding: "5px 14px", flexWrap: "wrap",
          boxSizing: "border-box",
        }}>
        {[
          { id: "listado", label: "Listado" },
          { id: "grupos", label: `Grupos${mercadoStats?.multi_portal ? ` (${mercadoStats.multi_portal})` : ""}`, hidden: !verGrupos },
          { id: "revision", label: `Revisión${(revisionTotal || mercadoStats?.revision_pendiente) ? ` (${revisionTotal || mercadoStats.revision_pendiente})` : ""}`, hidden: !verRevisar },
        ].filter(tab => !tab.hidden).map(tab => (
          <button key={tab.id} onClick={() => setVista(tab.id)} style={{
            padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
            fontFamily: "inherit",
            border: `1px solid ${vista === tab.id ? c.accentBlue : (isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.15)")}`,
            background: vista === tab.id ? c.accentBlue : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"),
            color: vista === tab.id ? "#fff" : (isDark ? "#fff" : c.textPrimary),
          }}>{tab.label}</button>
        ))}

        <button onClick={ejecutarMatching} disabled={matchRunning} style={{
          padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: matchRunning ? "wait" : "pointer",
          fontFamily: "inherit", border: `1px solid ${c.accentGreen}`, background: c.accentGreen + "22", color: c.accentGreen,
          opacity: matchRunning ? 0.6 : 1,
        }}>{matchRunning ? "⟳ Matching…" : "↻ Matching"}</button>

        <span style={{ fontSize: 12, color: c.textSecondary }}>
          <b style={{ color: isDark ? MERCADO_GREEN : '#000000' }}>{conteoMercado}</b>
          {loading && <span style={{ marginLeft: 6, opacity: 0.6 }}>↻</span>}
        </span>
        {verFiltroOrden && (
        <select value={order} onChange={e => setOrder(e.target.value)}
          style={{ ...sInput, width: 130, height: 26, fontSize: 11 }}>
          {ORDER_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        )}
        </div>
      </div>

      {(stats?.idealista_pendiente_tel > 0 || stats?.particulares_llamables > 0) && (
        <div style={{
          flexShrink: 0, padding: "8px 14px", fontSize: 11, lineHeight: 1.45,
          background: stats?.idealista_pendiente_tel > 0
            ? (isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)")
            : (isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)"),
          borderBottom: `1px solid ${c.sectionBorder}`,
          color: c.textSecondary,
        }}>
          {stats?.particulares_llamables > 0 && (
            <span style={{ marginRight: 12 }}>
              <strong style={{ color: "#22c55e" }}>{stats.particulares_llamables}</strong> particulares con teléfono
              {stats?.scope ? ` (${stats.scope})` : " (todos)"}
              {(stats?.precaptaciones_ana ?? 0) > 0 && (
                <> · <strong style={{ color: "#10b981" }}>{stats.precaptaciones_ana}</strong> precaptaciones Ana en Pipeline</>
              )}
              <span style={{ color: c.textMuted, fontSize: 10 }}> — una precaptación por vivienda; el mismo anuncio en varios portales no duplica</span>
            </span>
          )}
          {stats?.idealista_pendiente_tel > 0 && (
            <span>
              <strong style={{ color: "#ef4444" }}>{stats.idealista_pendiente_tel}</strong> Idealista sin teléfono —
              la extensión <em>Phone Bridge</em> los rellena sola (~1/min). Mantén Chrome con sesión Idealista abierta.
            </span>
          )}
        </div>
      )}

      {/* ── BODY: 50% / 50% ────────────────────────────────────────────────── */}
      {vista === "listado" && (
      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>

        {/* ════════════════════════════════════════
            PANEL IZQUIERDO — FILTROS
        ════════════════════════════════════════ */}
        <div style={{
          width: showPanelCoincidencias ? "32%" : "50%", flexShrink: 0,
          background: c.bgPanel,
          borderRight: `1px solid ${c.sectionBorder}`,
          display: "flex", flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}>
        <div style={{
          flex: 1, overflowY: "auto",
          padding: "12px 16px",
          gap: 12,
          display: "flex",
          flexDirection: "column",
        }}>

          {/* Portal — clic incluir, doble clic excluir (rojo) */}
          <div>
            <div style={sectionLabel}>Portal</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              <Chip
                label="Todos"
                active={!filtros.portales.length && !filtros.portalesExcl.length}
                onClick={limpiarPortales}
                theme={theme}
              />
              {Object.keys(PORTAL_CFG).filter(verPortal).map((key) => (
                <PortalFilterChip
                  key={key}
                  portalKey={key}
                  included={filtros.portales.includes(key)}
                  excluded={filtros.portalesExcl.includes(key)}
                  theme={theme}
                  onClick={() => onArrayInclExclChip("portales", "portalesExcl", key)}
                />
              ))}
            </div>
          </div>

          {/* Fila: Anunciante + Operación */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={sectionLabel}>Anunciante</div>
              <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                {[["", "Todos"], ["Particular", "Particular"], ["Agencia", "Agencia"]].map(([v, l]) => (
                  <Chip
                    key={v || "todos-anunciante"}
                    label={l}
                    active={v ? filtros.tipo_anunciante === v : (!filtros.tipo_anunciante && !filtros.tipo_anuncianteExcl)}
                    excluded={v ? filtros.tipo_anuncianteExcl === v : false}
                    onClick={() => (v
                      ? onSingleInclExclChip("tipo_anunciante", "tipo_anuncianteExcl", v)
                      : setFiltros((p) => ({ ...p, tipo_anunciante: "", tipo_anuncianteExcl: "" })))}
                    theme={theme}
                  />
                ))}
                <span style={{
                  width: 1, height: 22, flexShrink: 0,
                  background: c.border, margin: "0 8px",
                }} aria-hidden />
                <Chip
                  label="No tiene Vitalcasa"
                  active={filtros.sin_vitalcasa}
                  onClick={() => setFiltros((p) => ({ ...p, sin_vitalcasa: !p.sin_vitalcasa }))}
                  theme={theme}
                />
              </div>
            </div>
            <div>
              <div style={sectionLabel}>Operación</div>
              <div style={{ display: "flex", gap: 4 }}>
                {[["", "Todas"], ["venta", "Venta"], ["alquiler", "Alquiler"]].map(([v, l]) => (
                  <Chip
                    key={v || "todas-operacion"}
                    label={l}
                    active={v ? filtros.operacion === v : (!filtros.operacion && !filtros.operacionExcl)}
                    excluded={v ? filtros.operacionExcl === v : false}
                    onClick={() => (v
                      ? onSingleInclExclChip("operacion", "operacionExcl", v)
                      : setFiltros((p) => ({ ...p, operacion: "", operacionExcl: "" })))}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Fila: Tipo + Orientación */}
          {(verFiltroTipoInm || verFiltroOrientacion) && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {verFiltroTipoInm && (
            <div>
              <div style={sectionLabel}>Tipo inmueble</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {TIPOS_INM.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    small
                    active={filtros.tipo_inmueble === t}
                    excluded={filtros.tipo_inmuebleExcl === t}
                    onClick={() => onSingleInclExclChip("tipo_inmueble", "tipo_inmuebleExcl", t)}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
            )}
            {verFiltroOrientacion && (
            <div>
              <div style={sectionLabel}>Orientación</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {ORIENTACS.map(o => (
                  <Chip
                    key={o}
                    label={o}
                    small
                    active={filtros.orientacion === o}
                    excluded={filtros.orientacionExcl === o}
                    onClick={() => onSingleInclExclChip("orientacion", "orientacionExcl", o)}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
            )}
          </div>
          )}

          {/* Fila: Precio */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            <div>
              <div style={sectionLabel}>Precio mín.</div>
              <input
                type="number"
                placeholder="0 €"
                inputMode="numeric"
                autoComplete="off"
                value={filtros.precio_min}
                onChange={e => setF("precio_min", e.target.value)}
                onInput={e => setF("precio_min", e.target.value)}
                onBlur={e => setF("precio_min", e.target.value)}
                style={sInput}
              />
            </div>
            <div>
              <div style={sectionLabel}>Precio máx.</div>
              <input
                type="number"
                placeholder="∞ €"
                inputMode="numeric"
                autoComplete="off"
                value={filtros.precio_max}
                onChange={e => setF("precio_max", e.target.value)}
                onInput={e => setF("precio_max", e.target.value)}
                onBlur={e => setF("precio_max", e.target.value)}
                style={sInput}
              />
            </div>
            <div>
              <div style={sectionLabel}>m² mín.</div>
              <input type="number" placeholder="0" value={filtros.m2_min}
                onChange={e => setF("m2_min", e.target.value)} style={sInput} />
            </div>
            <div>
              <div style={sectionLabel}>m² máx.</div>
              <input type="number" placeholder="∞" value={filtros.m2_max}
                onChange={e => setF("m2_max", e.target.value)} style={sInput} />
            </div>
          </div>

          {/* Fila: Hab + Baños + Parcela */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <div style={sectionLabel}>Hab. mín.</div>
              <div style={{ display: "flex", gap: 3 }}>
                {["", "1", "2", "3", "4+"].map(n => (
                  <Chip
                    key={n || "hab-any"}
                    label={n || "–"}
                    small
                    active={filtros.hab_min === n}
                    excluded={filtros.hab_minExcl === n}
                    onClick={() => (n
                      ? onSingleInclExclChip("hab_min", "hab_minExcl", n)
                      : setFiltros((p) => ({ ...p, hab_min: "", hab_minExcl: "" })))}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
            <div>
              <div style={sectionLabel}>Baños mín.</div>
              <div style={{ display: "flex", gap: 3 }}>
                {["", "1", "2", "3+"].map(n => (
                  <Chip
                    key={n || "banos-any"}
                    label={n || "–"}
                    small
                    active={filtros.banos_min === n}
                    excluded={filtros.banos_minExcl === n}
                    onClick={() => (n
                      ? onSingleInclExclChip("banos_min", "banos_minExcl", n)
                      : setFiltros((p) => ({ ...p, banos_min: "", banos_minExcl: "" })))}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
            <div>
              <div style={sectionLabel}>Parcela mín (m²)</div>
              <input type="number" placeholder="0" value={filtros.parcela_min}
                onChange={e => setF("parcela_min", e.target.value)} style={sInput} />
            </div>
          </div>

          {/* Población — chips como FichaCliente */}
          {verFiltroPoblaciones && (
          <div>
            <div style={sectionLabel}>Población</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {POBLS_PRIN.map(p => (
                <Chip
                  key={p}
                  label={p}
                  active={filtros.pobls.includes(p)}
                  excluded={filtros.poblsExcl.includes(p)}
                  onClick={() => onArrayInclExclChip("pobls", "poblsExcl", p)}
                  theme={theme}
                />
              ))}
              <button type="button" onClick={() => setMasPobls(!masPobls)} style={{
                padding: "4px 10px", fontSize: 11, fontWeight: 500, borderRadius: 6, cursor: "pointer",
                border: `1px solid ${masPobls ? c.accentBlueBorder : c.border}`,
                background: masPobls ? c.accentBlueLight : c.chipBg,
                color: masPobls ? c.accentBlue : c.textMuted,
                transition: "all 0.15s", fontFamily: "inherit",
              }}>{masPobls ? "▾ Menos" : "▸ Más"}</button>
            </div>
            {masPobls && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                {POBLS_MAS.map(p => (
                  <Chip
                    key={p}
                    label={p}
                    active={filtros.pobls.includes(p)}
                    excluded={filtros.poblsExcl.includes(p)}
                    onClick={() => onArrayInclExclChip("pobls", "poblsExcl", p)}
                    theme={theme}
                  />
                ))}
              </div>
            )}
          </div>
          )}

          {/* Extras */}
          {verFiltroExtras && (
          <div>
            <div style={sectionLabel}>Extras</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {EXTRAS_LIST.map(([k, l]) => (
                <Chip
                  key={k}
                  label={l}
                  active={filtros[k]}
                  excluded={filtros[`${k}Excl`]}
                  onClick={() => onBoolTriChip(k, `${k}Excl`)}
                  theme={theme}
                />
              ))}
            </div>
          </div>
          )}
        </div>

          {/* Acciones rápidas — fijas al pie del panel izquierdo (como FichaCliente) */}
          <AccionesRapidasScrapingBar
            prop={propActiva}
            idsSeleccionados={idsSeleccionadosPipeline}
            onEnviadoPipeline={(patch) => {
              if (patch?.enviosById && Object.keys(patch.enviosById).length) {
                setItems((prev) => prev.map((it) => {
                  const envios = patch.enviosById[String(it.id)];
                  return envios ? { ...it, mi_pipeline_envios: envios } : it;
                }));
                setPropActiva((cur) => {
                  if (!cur?.id) return cur;
                  const envios = patch.enviosById[String(cur.id)];
                  return envios ? { ...cur, mi_pipeline_envios: envios } : cur;
                });
              }
              cargar(filtros, page, order);
              setSeleccionadas([]);
            }}
            onDescartado={(ids, mercadoIds) => {
              const idSet = new Set((ids || []).map(String));
              const mercadoSet = new Set((mercadoIds || []).map(String));
              const seVa = (it) =>
                idSet.has(String(it?.id))
                || (it?.id_propiedad_mercado && mercadoSet.has(String(it.id_propiedad_mercado)));
              setItems((prev) => prev.filter((it) => !seVa(it)));
              setPropActiva((cur) => (seVa(cur) ? null : cur));
              setModalProp((cur) => (seVa(cur) ? null : cur));
              setSeleccionadas((prev) => prev.filter((id) => !idSet.has(String(id)) && !mercadoSet.has(String(id))));
              loadMercadoStats();
            }}
            theme={theme}
            sectionLabel={sectionLabel}
          />
        </div>

        {/* ════════════════════════════════════════
            PANEL CENTRAL — LISTADO
        ════════════════════════════════════════ */}
        <div style={{
          width: showPanelCoincidencias ? "38%" : "50%",
          display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden", background: c.bgPanel,
        }}>

          {/* Sub-header: conteo · acción vincular (centro) · paginación */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center", gap: 8,
            padding: "5px 12px", borderBottom: `1px solid ${c.sectionBorder}`,
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, justifySelf: "start" }}>
              <span style={{ color: isDark ? MERCADO_GREEN : '#000000', fontWeight: 700 }}>{conteoMercado}</span>
              {seleccionadas.length > 0 && (
                <span style={{ marginLeft: 8, fontSize: 11, color: c.accentBlue }}>
                  · {seleccionadas.length} sel.
                </span>
              )}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 6, justifySelf: "center" }}>
              {!modoVincularPanel && seleccionadas.length >= 1 && (
                <button type="button" onClick={abrirModoVincular} style={{
                  padding: "5px 16px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                  border: "none", background: c.accentBlue, color: "#fff",
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                  Vincular
                </button>
              )}
              {modoVincularPanel && (
                <>
                  {idsOrigenVincular.length >= 2 && (
                    <button type="button" disabled={vincularBusy} onClick={crearGrupoSeleccion} style={{
                      padding: "5px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                      border: "none", background: "#22c55e", color: "#fff",
                      cursor: vincularBusy ? "wait" : "pointer", fontFamily: "inherit",
                      opacity: vincularBusy ? 0.7 : 1,
                    }}>
                      {vincularBusy ? "…" : `Crear grupo (${idsOrigenVincular.length})`}
                    </button>
                  )}
                  <button type="button" onClick={cerrarModoVincular} style={{
                    padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                    border: `1px solid ${c.border}`, background: "transparent", color: c.textMuted,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                    ✕ Cerrar
                  </button>
                </>
              )}
              {vincularMsg && (
                <span style={{ fontSize: 10, color: vincularMsg.includes("Error") || vincularMsg.includes("No ") ? "#ef4444" : "#22c55e" }}>
                  {vincularMsg}
                </span>
              )}
            </div>

            <div style={{ justifySelf: "end", display: "flex", alignItems: "center", gap: 6 }}>
              {totalPages > 1 && (
                <>
                  <button onClick={() => { const np = Math.max(1, page - 1); setPage(np); cargar(filtros, np, order); }}
                    disabled={page <= 1}
                    style={{ fontSize: 11, padding: "2px 8px", borderRadius: 5, border: `1px solid ${c.border}`, background: c.chipBg, color: c.textSecondary, cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.4 : 1, fontFamily: "inherit" }}>‹</button>
                  <span style={{ fontSize: 11, color: c.textSecondary }}>{page} / {totalPages}</span>
                  <button onClick={() => { const np = Math.min(totalPages, page + 1); setPage(np); cargar(filtros, np, order); }}
                    disabled={page >= totalPages}
                    style={{ fontSize: 11, padding: "2px 8px", borderRadius: 5, border: `1px solid ${c.border}`, background: c.chipBg, color: c.textSecondary, cursor: page >= totalPages ? "default" : "pointer", opacity: page >= totalPages ? 0.4 : 1, fontFamily: "inherit" }}>›</button>
                </>
              )}
            </div>
          </div>

          {/* Grid tarjetas */}
          <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
            {loading && items.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: c.textMuted }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 28, animation: "sp-spin 1s linear infinite", display: "block", marginBottom: 8 }}>⟳</div>
                  <p style={{ fontSize: 13 }}>Cargando propiedades...</p>
                </div>
              </div>
            ) : error ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: c.textMuted }}>
                <div style={{ textAlign: "center", maxWidth: 400 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: "#ef4444", marginBottom: 8 }}>⚠ Error al cargar propiedades</p>
                  <p style={{ fontSize: 11, color: c.textMuted, wordBreak: "break-all" }}>{error}</p>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: c.textMuted }}>
                <div style={{ textAlign: "center" }}>
                  <svg style={{ margin: "0 auto 12px", opacity: 0.3 }} width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  <p style={{ fontWeight: 500, fontSize: 14, color: c.textSecondary }}>Sin resultados</p>
                  <p style={{ fontSize: 12, marginTop: 4 }}>Ajusta los filtros de búsqueda</p>
                </div>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 12,
                alignContent: "start",
              }}>
                {items.map(p => (
                  <TarjetaPropiedad key={propiedadId(p)} p={p}
                    seleccionada={seleccionadas.includes(propiedadId(p))}
                    activa={propActiva && propiedadId(propActiva) === propiedadId(p)}
                    onToggle={toggleSel}
                    onSelect={() => seleccionarProp(p)}
                    onOpen={() => { seleccionarProp(p); setModalProp(p); }}
                    theme={theme} />
                ))}
              </div>
            )}
          </div>
        </div>

        {showPanelCoincidencias && (
          <div style={{ width: "30%", flexShrink: 0, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <PanelCoincidenciasMercado
              theme={theme}
              origenIds={idsOrigenVincular}
              origenLabel={propActiva?.titulo || propActiva?.direccion || ""}
              seedAnuncio={propActiva}
              onVinculado={refreshTrasVincular}
              onOpenAd={setModalProp}
            />
          </div>
        )}
      </div>
      )}

      {vista === "grupos" && (
        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: c.bgPanel }}>
          <p style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
            Inmuebles detectados en <b>varios portales</b> (score ≥ 85 auto-match).{" "}
            <b>{gruposTotal}</b> grupos multi-portal.
          </p>
          {loading && grupos.length === 0 ? (
            <p style={{ color: c.textMuted }}>Cargando grupos…</p>
          ) : grupos.length === 0 ? (
            <p style={{ color: c.textMuted }}>Sin grupos. Pulsa <b>↻ Matching</b> para analizar.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {grupos.map(g => (
                <GrupoMercadoCard key={g.id} g={g} theme={theme} onOpenAd={setModalProp} />
              ))}
            </div>
          )}
        </div>
      )}

      {vista === "revision" && (
        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: c.bgPanel }}>
          <p style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
            Desmarca los que <b>no</b> sean la misma vivienda y confirma solo la selección.{" "}
            <b>{revisionTotal}</b> grupos pendientes.
          </p>
          {revisionMsg && (
            <div style={{
              marginBottom: 12, padding: "8px 12px", borderRadius: 8, fontSize: 12,
              background: revisionMsg.includes("No se") || revisionMsg.includes("Selecciona") || revisionMsg.includes("Error")
                ? "rgba(239,68,68,0.12)" : "rgba(34,197,94,0.12)",
              color: revisionMsg.includes("No se") || revisionMsg.includes("Selecciona") || revisionMsg.includes("Error")
                ? "#ef4444" : "#16a34a",
              border: `1px solid ${revisionMsg.includes("No se") || revisionMsg.includes("Selecciona") || revisionMsg.includes("Error") ? "rgba(239,68,68,0.35)" : "rgba(34,197,94,0.35)"}`,
            }}>
              {revisionMsg}
            </div>
          )}
          {loading && revision.length === 0 ? (
            <p style={{ color: c.textMuted }}>Cargando bandeja…</p>
          ) : revision.length === 0 ? (
            <p style={{ color: c.textMuted }}>Bandeja vacía.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {revision.map(r => (
                <RevisionCard key={r.id} r={r} theme={theme}
                  busy={resolvingRevisionId === r.id}
                  puedeModificar={modRevisar}
                  onConfirm={(ids) => resolverRevision(r.id, "confirmar", ids)}
                  onReject={() => resolverRevision(r.id, "rechazar")}
                  onOpenAd={setModalProp} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── MODAL DETALLE PROPIEDAD ────────────────────────────────────────── */}
      {modalProp && (
        <ModalDetallePropiedad p={modalProp} theme={theme}
          onClose={() => setModalProp(null)}
          onFocusVincular={(p) => {
            setPropActiva(p);
            const pid = p?.id ?? p?.id_propiedad_mercado;
            if (pid) setSeleccionadas([pid]);
            setModoVincularPanel(true);
            setModalProp(null);
          }}
          onUpdated={() => {
            loadMercadoStats();
            if (vista === "listado") cargar(filtros, page, order);
            else if (vista === "grupos") cargarGrupos(gruposPage);
            else if (vista === "revision") cargarRevision();
          }} />
      )}

      {verAlertas && (
        <ModalAlertasMarketplace
          open={modalAlertas}
          onClose={() => setModalAlertas(false)}
          filtrosActuales={filtros}
        />
      )}

      <style>{`
        @keyframes sp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── Tarjeta de propiedad — idéntica en diseño a FichaCliente ─────────────────
function contactoDesdeScraping(p) {
  const tel = p.telefonos?.[0] || "";
  const email = p.emails?.[0] || "";
  const nom = (p.nombre_contacto || p.alias_agencia || "Contacto").trim();
  const parts = nom.split(/\s+/).filter(Boolean);
  return {
    nombre: nom,
    telefono: tel,
    email,
    iniciales: parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (parts[0]?.[0] || "C").toUpperCase(),
  };
}

function scrapingParaImprimir(p) {
  const car = p.caracteristicas || {};
  return {
    titulo: p.titulo || "Sin título",
    precio: p.precio_numerico || p.precio,
    tipo: p.tipo_inmueble || car.tipo_propiedad,
    poblacion: p.municipio,
    operacion: p.operacion || car.operacion || "Venta",
    habitaciones: p.habitaciones || car.habitaciones,
    banos: p.banos || car.banos,
    m2: p.superficie_m2 || car.superficie_m2,
    planta: p.planta ?? car.planta,
    direccion: p.direccion,
    propietario: p.nombre_contacto || p.alias_agencia,
    fotos: Array.isArray(p.fotos) ? p.fotos : [],
    piscina: p.piscina,
    amueblado: p.amueblado,
    primera_linea: p.primera_linea,
    descripcion: p.descripcion,
  };
}

function AccionesRapidasScrapingBar({ prop, idsSeleccionados, onEnviadoPipeline, onDescartado, theme, sectionLabel }) {
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const dialogs = useDialogs();
  const relacionContacto = useCaptacionRelacionContacto();
  const [modalWa, setModalWa] = useState(false);
  const [modalEmail, setModalEmail] = useState(false);
  const [modalNota, setModalNota] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [llamando, setLlamando] = useState(false);
  const [enviandoPipeline, setEnviandoPipeline] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const [captacionPipelineId, setCaptacionPipelineId] = useState(null);

  useEffect(() => {
    setCaptacionPipelineId(null);
  }, [prop?.id]);

  const contacto = useMemo(() => (prop ? contactoDesdeScraping(prop) : null), [prop]);
  const scrapingId = prop?.id;

  const asegurarCopiaPipeline = useCallback(async () => {
    if (!scrapingId) return null;
    if (captacionPipelineId) return captacionPipelineId;
    const { data } = await api.post(`/scraping/resultados/${scrapingId}/enviar-pipeline`);
    setCaptacionPipelineId(data.captacion_id);
    return data.captacion_id;
  }, [captacionPipelineId, scrapingId]);

  const ejecutarLlamada = useCallback(async () => {
    if (!contacto?.telefono || llamando) return;
    setLlamando(true);
    try {
      await llamarContacto({ telefono: contacto.telefono, nombre: contacto.nombre });
      await dialogs.alert("📞 Llamada iniciada — tu móvil recibirá la llamada en breve", { autoCloseMs: 2000 });
    } catch (err) {
      await dialogs.alert(err.message || "Error al llamar");
    } finally {
      setLlamando(false);
    }
  }, [contacto, llamando, dialogs]);

  // Si hay más de una propiedad marcada con checkbox, se envían todas; si no, solo la activa (scrapingId).
  const idsAEnviar = useMemo(() => (
    (idsSeleccionados && idsSeleccionados.length > 1)
      ? idsSeleccionados
      : (scrapingId ? [scrapingId] : [])
  ), [idsSeleccionados, scrapingId]);

  const avisarPipelineRefresco = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent("vc-mp-enviado-pipeline"));
    } catch (_) { /* ignore */ }
  }, []);

  const enviarAPipeline = useCallback(async () => {
    if (!idsAEnviar.length || enviandoPipeline) return;
    setEnviandoPipeline(true);
    const enviosById = {};
    const markEnvio = (id, data) => {
      const veces = Math.max(1, Number(data?.veces_enviado) || 1);
      const ultima = data?.ultima_fecha || new Date().toISOString();
      const primera = data?.primera_fecha || ultima;
      const arr = new Array(veces).fill(primera);
      arr[veces - 1] = ultima;
      enviosById[String(id)] = arr;
    };
    try {
      if (idsAEnviar.length === 1) {
        const { data } = await api.post(`/scraping/resultados/${idsAEnviar[0]}/enviar-pipeline`);
        setCaptacionPipelineId(data.captacion_id);
        markEnvio(idsAEnviar[0], data);
        avisarPipelineRefresco();
        if (data.already_exists) {
          await dialogs.alert(`Esta propiedad ya está en tu Pipeline${data.veces_enviado > 1 ? ` (enviada ${data.veces_enviado} veces)` : ""}.`, { autoCloseMs: 2500 });
          const tel = prop?.telefonos?.[0] || "";
          const em = prop?.emails?.[0] || "";
          if (tel || em) {
            await relacionContacto.checkNow(tel, em, {
              excludeCaptacionId: data.captacion_id,
              force: true,
            });
          }
        } else {
          await dialogs.alert("✓ Enviada a tu Pipeline (Precaptación)", { autoCloseMs: 2500 });
          const coinc = data.coincidencias_contacto || [];
          if (coinc.length) {
            const tel = prop?.telefonos?.[0] || "";
            const em = prop?.emails?.[0] || "";
            relacionContacto.showFromServer(coinc, tel, em);
          }
        }
        onEnviadoPipeline?.({ enviosById });
        return;
      }

      let nuevas = 0, existentes = 0, errores = 0;
      let primeraCoinc = null;
      let primeraTel = "";
      let primeraEmail = "";
      for (const id of idsAEnviar) {
        try {
          const { data } = await api.post(`/scraping/resultados/${id}/enviar-pipeline`);
          markEnvio(id, data);
          if (data.already_exists) existentes += 1; else nuevas += 1;
          if (!primeraCoinc && Array.isArray(data.coincidencias_contacto) && data.coincidencias_contacto.length) {
            primeraCoinc = data.coincidencias_contacto;
            const src = id === scrapingId ? prop : null;
            primeraTel = src?.telefonos?.[0] || "";
            primeraEmail = src?.emails?.[0] || "";
          }
        } catch {
          errores += 1;
        }
      }
      if (nuevas + existentes > 0) avisarPipelineRefresco();
      const partes = [];
      if (nuevas)     partes.push(`${nuevas} nueva${nuevas === 1 ? "" : "s"}`);
      if (existentes) partes.push(`${existentes} ya estaba${existentes === 1 ? "" : "n"} en tu Pipeline`);
      if (errores)    partes.push(`${errores} con error`);
      await dialogs.alert(`✓ Pipeline — ${idsAEnviar.length} propiedades: ${partes.join(", ")}`, { autoCloseMs: 3500 });
      if (primeraCoinc?.length) {
        relacionContacto.showFromServer(primeraCoinc, primeraTel, primeraEmail);
      }
      onEnviadoPipeline?.({ enviosById });
    } catch (err) {
      await dialogs.alert(err.response?.data?.error || err.message || "Error al enviar al Pipeline");
    } finally {
      setEnviandoPipeline(false);
    }
  }, [enviandoPipeline, idsAEnviar, dialogs, onEnviadoPipeline, prop, relacionContacto, scrapingId, avisarPipelineRefresco]);

  const descartarAnuncios = useCallback(async () => {
    if (!idsAEnviar.length || descartando) return;
    const n = idsAEnviar.length;
    const motivo = await dialogs.prompt(
      n === 1
        ? "El anuncio saldrá de MarketPlace y de los pipelines de los agentes. Quedará en Captaciones eliminadas."
        : `Los ${n} anuncios saldrán de MarketPlace y de los pipelines de los agentes. Quedarán en Captaciones eliminadas.`,
      {
        inputLabel: "Motivo de la eliminación",
        defaultValue: "Propietario indica que ya se vendió; el anuncio sigue publicado",
        required: true,
      },
    );
    if (!motivo) return;
    setDescartando(true);
    const descartados = [];
    const mercadoIds = [];
    let errores = 0;
    let primerError = "";
    let pipelinesArchivados = 0;
    try {
      for (const id of idsAEnviar) {
        try {
          const { data } = await api.patch(`/scraping/resultados/${id}/descartar`, { motivo });
          const idsOk = Array.isArray(data?.ids) && data.ids.length ? data.ids : [id];
          descartados.push(...idsOk);
          if (data?.id_propiedad_mercado) mercadoIds.push(data.id_propiedad_mercado);
          pipelinesArchivados += Number(data?.pipelines_archivados) || 0;
        } catch (err) {
          errores += 1;
          if (!primerError) primerError = err.response?.data?.error || err.message || "Error al descartar";
        }
      }
      if (descartados.length || mercadoIds.length) onDescartado?.(descartados, mercadoIds);
      if (errores) {
        await dialogs.alert(primerError || `${descartados.length} descartado(s), ${errores} con error.`);
      } else {
        const extraPip = pipelinesArchivados
          ? ` · ${pipelinesArchivados} en Eliminados de pipeline`
          : "";
        await dialogs.alert(
          n === 1
            ? `Anuncio descartado — no volverá a captarse${extraPip}`
            : `${descartados.length} anuncios descartados${extraPip}`,
          { autoCloseMs: 2800 },
        );
      }
    } finally {
      setDescartando(false);
    }
  }, [descartando, dialogs, idsAEnviar, onDescartado]);

  const handleNotaSave = useCallback(async ({ contenido }) => {
    try {
      const capId = await asegurarCopiaPipeline();
      if (!capId) return;
      await api.post(`/captaciones/${capId}/historial`, { tipo: "nota", contenido });
      await dialogs.alert("Nota guardada en tu Pipeline", { autoCloseMs: 2000 });
    } catch (err) {
      await dialogs.alert(err.response?.data?.error || err.message || "No se pudo guardar la nota");
    }
  }, [asegurarCopiaPipeline, dialogs]);

  const sinProp = !prop;
  const btnBase = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    padding: "6px 0",
    borderRadius: 6,
    transition: "all 0.15s",
    border: `1px solid ${c.btnActionBorder}`,
    background: c.btnActionBg,
    fontSize: 10,
    fontWeight: 500,
    color: c.btnActionText,
    fontFamily: "inherit",
  };

  return (
    <>
      <div style={{ flexShrink: 0, padding: "6px 16px 8px", borderTop: `1px solid ${c.sectionBorder}`, background: c.bgPanel }}>
        <div style={{ ...sectionLabel, marginBottom: 2 }}>Acciones rápidas</div>
        {sinProp && (
          <p style={{ fontSize: 10, color: isDark ? c.textMuted : '#000000', margin: "0 0 6px" }}>
            Marca el checkbox o pulsa un anuncio para activar las acciones
          </p>
        )}
        {!sinProp && (
          <p style={{
            fontSize: 10, color: c.textSecondary, margin: "0 0 6px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {prop.titulo || "Sin título"}
            {prop.municipio ? ` · ${prop.municipio}` : ""}
          </p>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          {[
            {
              l: "Llamar",
              bg: c.btnCallBg,
              sh: c.btnCallShadow,
              disabled: sinProp || !contacto?.telefono || llamando,
              onClick: ejecutarLlamada,
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={c.btnCallIconStroke || "#dc2626"} strokeWidth="2.2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>,
            },
            {
              l: "WhatsApp",
              bg: c.btnWhatsappBg,
              sh: c.btnWhatsappShadow,
              disabled: sinProp || !contacto?.telefono,
              onClick: () => setModalWa(true),
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" /></svg>,
            },
            {
              l: "Email",
              bg: c.btnEmailBg || "#b45309",
              sh: c.btnEmailShadow || "0 2px 6px rgba(180,83,9,0.25)",
              disabled: sinProp || !contacto?.email,
              onClick: () => setModalEmail(true),
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>,
            },
            {
              l: "Imprimir",
              bg: "linear-gradient(135deg,#475569,#334155)",
              sh: "0 2px 6px rgba(71,85,105,0.3)",
              disabled: sinProp,
              onClick: () => setModalPrint(true),
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>,
            },
            {
              l: "Nota",
              bg: c.btnNotaBg,
              sh: c.btnNotaShadow,
              disabled: sinProp,
              onClick: () => setModalNota(true),
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
            },
            {
              l: idsAEnviar.length > 1 ? `Mi Pipeline (${idsAEnviar.length})` : "Mi Pipeline",
              title: idsAEnviar.length > 1 ? `Enviar ${idsAEnviar.length} propiedades a mi Pipeline` : "Enviar a mi Pipeline",
              bg: "linear-gradient(135deg,#6366f1,#4f46e5)",
              sh: "0 2px 6px rgba(99,102,241,0.35)",
              disabled: !idsAEnviar.length || enviandoPipeline,
              onClick: enviarAPipeline,
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>,
            },
            {
              l: idsAEnviar.length > 1 ? `No captar (${idsAEnviar.length})` : "No captar",
              title: idsAEnviar.length > 1
                ? `Descartar ${idsAEnviar.length} anuncios — no volverán a MarketPlace ni a precaptación`
                : "Descartar — no volverá a MarketPlace ni a precaptación automática",
              bg: "linear-gradient(135deg,#ef4444,#dc2626)",
              sh: "0 2px 6px rgba(239,68,68,0.35)",
              disabled: !idsAEnviar.length || descartando,
              onClick: descartarAnuncios,
              ic: <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/></svg>,
            },
          ].map((a) => (
            <button
              key={a.l}
              type="button"
              title={a.title || a.l}
              disabled={a.disabled}
              onClick={a.onClick}
              style={{
                ...btnBase,
                cursor: a.disabled ? "not-allowed" : "pointer",
                opacity: a.disabled ? 0.45 : 1,
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: 6,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: a.bg, boxShadow: a.sh,
              }}>
                {a.ic}
              </div>
              {a.l}
            </button>
          ))}
        </div>
      </div>

      {modalWa && contacto && (
        <ModalWhatsApp
          isOpen={modalWa}
          contact={contacto}
          onClose={() => setModalWa(false)}
          onSend={() => setModalWa(false)}
        />
      )}
      {modalEmail && contacto && (
        <ModalEmail
          isOpen={modalEmail}
          contact={contacto}
          onClose={() => setModalEmail(false)}
          onSend={() => setModalEmail(false)}
        />
      )}
      {modalNota && contacto && (
        <ModalNota
          isOpen={modalNota}
          onClose={() => setModalNota(false)}
          onSave={handleNotaSave}
          contact={{ nombre: contacto.nombre, iniciales: contacto.iniciales }}
        />
      )}
      {modalPrint && prop && (
        <ModalImprimirPropiedades
          isOpen={modalPrint}
          onClose={() => setModalPrint(false)}
          propiedades={[scrapingParaImprimir(prop)]}
        />
      )}
      <ModalRelacionContactoExistente
        open={relacionContacto.open}
        matches={relacionContacto.matches}
        canal={relacionContacto.canal}
        onClose={relacionContacto.dismiss}
        onDescartar={relacionContacto.dismissDiscard}
      />
    </>
  );
}

/** Urbanización / partida en tags de ficha Idealista (no suele ir en el título del listado). */
function urbDesdeTagsPortal(p) {
  const tags = p?.caracteristicas?.tags_portal;
  if (!Array.isArray(tags)) return null;
  const hit = tags.find((t) => {
    const s = String(t || "").trim();
    return /^(urb\.?|urbanizaci[oó]n)\b/i.test(s) || /\bpartida\s+\S/i.test(s);
  });
  return hit ? String(hit).trim() : null;
}

function plantaTarjetaNoEsSuperficie(p) {
  const planta = p?.planta != null ? String(p.planta).trim() : "";
  if (!planta) return null;
  const m2 = Number(p.superficie_m2);
  const nPlanta = parseInt(planta.replace(/\D/g, ""), 10);
  if (Number.isFinite(m2) && m2 >= 20 && nPlanta === Math.round(m2)) return null;
  return planta;
}

function TarjetaPropiedad({ p, seleccionada, activa, onToggle, onSelect, onOpen, theme }) {
  const c  = theme.colors;
  const isDark = theme.name === "dark";
  const fotos = Array.isArray(p.fotos) ? p.fotos : [];
  const esAlquiler = p.operacion === "Alquiler";
  const numAnuncios = p.mercado_num_anuncios || 1;
  const portalesGrupo = Array.isArray(p.mercado_portales) && p.mercado_portales.length
    ? p.mercado_portales
    : (numAnuncios > 1 ? [] : [p.portal].filter(Boolean));
  const urbTag = urbDesdeTagsPortal(p);
  const plantaOk = plantaTarjetaNoEsSuperficie(p);
  const precioLabel = (() => {
    if ((p.precio_a_consultar || p.precio === 'A consultar') && !p.precio_numerico) return 'A consultar';
    // Precio del anuncio mostrado (no el min/max del grupo): evita que un Habitaclia
    // con precio corrupto o un hermano barato pinte un rango engañoso en la tarjeta.
    if (p.precio_numerico) return `${Number(p.precio_numerico).toLocaleString("es-ES")} €`;
    const min = p.mercado_precio_min;
    const max = p.mercado_precio_max;
    if (min && max && Number(min) !== Number(max)) {
      return `${Number(min).toLocaleString("es-ES")} – ${Number(max).toLocaleString("es-ES")} €`;
    }
    return p.precio || "Sin precio";
  })();

  const fechaStr = (() => {
    const d = p.fecha_publicacion || p.created_at;
    if (!d) return null;
    return new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
  })();
  const horaStr = p.created_at
    ? new Date(p.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div style={{
      background: c.propCardBg, borderRadius: 10,
      border: `2px solid ${activa ? c.accentBlue : seleccionada ? c.accentBlue : c.propCardBorder}`,
      overflow: "hidden", boxShadow: activa ? c.propCardHoverShadow : c.propCardShadow,
      transition: "box-shadow 0.2s, transform 0.2s, border-color 0.15s", cursor: "pointer",
    }}
      onMouseEnter={e => { if (!activa) { e.currentTarget.style.boxShadow = c.propCardHoverShadow; e.currentTarget.style.transform = "translateY(-2px)"; } }}
      onMouseLeave={e => { if (!activa) { e.currentTarget.style.boxShadow = c.propCardShadow; e.currentTarget.style.transform = "none"; } }}
      onClick={() => { onSelect?.(); onOpen?.(); }}
    >
      {/* Foto 16:9 */}
      <div style={{ position: "relative", aspectRatio: "16/9", background: c.bgCard }}>
        {fotos.length > 0 ? (
          <img src={proxyFoto(fotos[0])} alt={p.titulo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
              const ph = e.target.nextElementSibling;
              if (ph) ph.style.display = "flex";
            }} />
        ) : null}
        <div style={{
          position: "absolute", inset: 0, display: fotos.length > 0 ? "none" : "flex",
          alignItems: "center", justifyContent: "center", color: c.textPlaceholder,
        }}>
          <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
        </div>

        {/* Checkbox selección */}
        <div onClick={e => { e.stopPropagation(); onToggle(p); }} style={{
          position: "absolute", top: 8, left: 8, zIndex: 6,
          width: 22, height: 22, borderRadius: 5,
          border: `2px solid ${seleccionada ? "#fff" : "rgba(255,255,255,0.8)"}`,
          background: seleccionada ? c.accentBlue : "rgba(0,0,0,0.25)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "all 0.15s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}>
          {seleccionada && <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
        </div>

        {/* Etiquetas portal */}
        <div style={{ position: "absolute", top: 8, left: 36, zIndex: 6, display: "flex", gap: 4, flexWrap: "wrap", maxWidth: "calc(100% - 80px)" }}>
          {portalesGrupo.length > 0
            ? portalesGrupo.map((portal) => <PortalLabel key={portal} portal={portal} />)
            : (p.portal ? <PortalLabel portal={p.portal} /> : null)}
        </div>

        {/* Badge precio */}
        <div style={{
          position: "absolute", bottom: 8, left: 8, zIndex: 6,
          background: c.propBadgeBg, color: c.propBadgeText,
          padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700,
        }}>
          {precioLabel}
        </div>

        {/* Badge operación */}
        <div style={{
          position: "absolute", top: 8, right: 8, zIndex: 6,
          padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#fff",
          background: esAlquiler ? c.accentPink : c.accentGreen,
        }}>{p.operacion || "Venta"}</div>

        {/* Badge particular */}
        {p.tipo_anunciante === "Particular" && (
          <div style={{
            position: "absolute", top: 30, right: 8, zIndex: 6,
            padding: "2px 6px", borderRadius: 5, fontSize: 9, fontWeight: 700, color: "#fff",
            background: "rgba(245,158,11,0.9)",
          }}>Part.</div>
        )}

        {/* Etiqueta Pipeline — overlay como «✓ Enviada» en ficha de clientes */}
        <div style={{
          position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 7,
        }}>
          <BadgePipelineUsuario p={p} theme={theme} compact />
        </div>

        {/* Fecha y hora */}
        {fechaStr && (
          <div style={{
            position: "absolute", bottom: 8, right: 8, zIndex: 6,
            background: "rgba(0,0,0,0.5)", color: "rgba(255,255,255,0.9)",
            padding: "2px 6px", borderRadius: 4, fontSize: 9, backdropFilter: "blur(4px)",
          }}>
            {fechaStr}{horaStr ? ` ${horaStr}` : ""}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: 12 }}>
        <h3 style={{
          fontSize: 13, fontWeight: 700, color: c.textPrimary,
          margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{p.titulo || "Sin título"}</h3>
        <p style={{ fontSize: 11, color: c.textMuted, margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {[p.tipo_inmueble, urbTag, p.municipio || p.direccion].filter(Boolean).join(" · ") || "—"}
        </p>

        {/* Estadísticas */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, fontSize: 11, color: c.textSecondary }}>
          {p.habitaciones > 0 && <span>🛏 {p.habitaciones} hab</span>}
          {p.banos > 0        && <span>🚿 {p.banos} baños</span>}
          {p.superficie_m2 > 0 && <span>📐 {Math.round(p.superficie_m2)} m²</span>}
          {plantaOk && <span>🏢 P.{plantaOk}</span>}
        </div>

        {/* Extras */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
          {p.piscina       && <ExtraBadge label="🏊 Piscina"  c={c} />}
          {p.garaje        && <ExtraBadge label="🚗 Garaje"   c={c} />}
          {p.terraza       && <ExtraBadge label="🌿 Terraza"  c={c} />}
          {p.ascensor      && <ExtraBadge label="🛗 Ascensor" c={c} />}
          {p.amueblado     && <ExtraBadge label="🪑 Amueblado" c={c} />}
          {p.primera_linea && <ExtraBadge label="🏖️ 1ª línea" c={c} />}
          {p.vista_mar     && <ExtraBadge label="🌊 Vista mar" c={c} />}
          {p.pista_padel   && <ExtraBadge label="🎾 Pádel"   c={c} />}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8,
          marginTop: 8, paddingTop: 8,
          borderTop: `1px solid ${c.borderLight || c.propCardBorder}`,
        }}>
          <span style={{
            fontSize: 10, color: c.textMuted,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
          }}>
            {p.alias_agencia || p.nombre_contacto || "—"}
          </span>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
            {(numAnuncios >= 2) && (
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "4px 8px", borderRadius: 6,
                border: `1px solid ${isDark ? "rgba(34,197,94,0.45)" : "rgba(34,197,94,0.35)"}`,
                background: isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.06)",
              }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" aria-hidden>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="13" x2="15" y2="13" />
                  <line x1="9" y1="17" x2="13" y2="17" />
                </svg>
                <div style={{ lineHeight: 1.15 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#22c55e", letterSpacing: "0.02em" }}>
                    {numAnuncios} ANUNCIOS
                  </div>
                  <div style={{ fontSize: 8, fontWeight: 600, color: c.textMuted, letterSpacing: "0.06em" }}>
                    {portalesGrupo.length ? `${portalesGrupo.length} PORTALES` : "MISMA VIVIENDA"}
                  </div>
                </div>
              </div>
            )}
            <BadgePosibleAgencia p={p} theme={theme} compact />
            <BadgeTelefonoPendienteIdealista p={p} theme={theme} compact />
            <BadgePrecaptacionAna p={p} theme={theme} compact />
            {p.referencia && (
              <span style={{ fontSize: 10, color: c.textMuted, whiteSpace: "nowrap" }}>
                Ref: <b style={{ color: c.textSecondary }}>{String(p.referencia).slice(0, 12)}</b>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function ExtraBadge({ label, c }) {
  return (
    <span style={{
      padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 500,
      background: c.propExtraBg, color: c.propExtraText,
    }}>{label}</span>
  );
}

function GrupoMercadoCard({ g, theme, onOpenAd }) {
  const c = theme.colors;
  const dc = g.datos_canonicos || {};
  const anuncios = Array.isArray(g.anuncios) ? g.anuncios : [];
  const foto = anuncios.find(a => a.fotos?.length)?.fotos?.[0];

  return (
    <div style={{
      border: `1px solid ${c.propCardBorder}`, borderRadius: 10, overflow: "hidden",
      background: c.propCardBg, display: "flex", gap: 12, padding: 12,
    }}>
      <div style={{ width: 120, flexShrink: 0, aspectRatio: "4/3", borderRadius: 8, overflow: "hidden", background: c.bgCard }}>
        {foto ? <img src={proxyFoto(foto)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{dc.titulo || "Sin título"}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#6366f1", padding: "2px 8px", borderRadius: 6 }}>
            {g.num_portales} portales
          </span>
          {g.score_confianza_match && (
            <span style={{ fontSize: 10, color: c.textMuted }}>score {g.score_confianza_match}</span>
          )}
        </div>
        <p style={{ fontSize: 11, color: c.textMuted, margin: "4px 0" }}>
          {[dc.municipio, dc.superficie_m2 && `${dc.superficie_m2} m²`, dc.habitaciones && `${dc.habitaciones} hab`].filter(Boolean).join(" · ")}
          {g.precio_minimo && ` — ${Number(g.precio_minimo).toLocaleString("es-ES")}€`}
          {g.precio_maximo && g.precio_maximo !== g.precio_minimo && ` – ${Number(g.precio_maximo).toLocaleString("es-ES")}€`}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          {anuncios.map(a => (
            <button key={a.id} type="button" onClick={() => onOpenAd(a)} style={{
              display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6,
              border: `1px solid ${c.border}`, background: c.chipBg, cursor: "pointer", fontFamily: "inherit",
            }}>
              <PortalBadge portal={a.portal} size={14} />
              <span style={{ fontSize: 10, color: c.textSecondary }}>
                {a.precio_numerico ? `${Number(a.precio_numerico).toLocaleString("es-ES")}€` : "—"}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function RevisionFotoVisor({ fotos, adKey, theme }) {
  const c = theme.colors;
  const list = Array.isArray(fotos) ? fotos.filter(Boolean) : [];
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  const fotoSig = `${adKey || ""}|${list.join("\n")}`;

  useEffect(() => {
    setIdx(0);
    setLoaded(false);
  }, [fotoSig]);

  const currentSrc = list[idx] ? proxyFoto(list[idx]) : "";

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, [currentSrc, idx, fotoSig]);

  const bindImg = (el) => {
    imgRef.current = el;
    if (el?.complete && el.naturalWidth > 0) setLoaded(true);
  };

  if (!list.length) {
    return (
      <div style={{
        width: "100%", aspectRatio: "16/10", borderRadius: 8, background: c.bgCard,
        display: "flex", alignItems: "center", justifyContent: "center", color: c.textPlaceholder,
      }}>
        <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
        </svg>
      </div>
    );
  }

  const go = (next) => { setLoaded(false); setIdx(next); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{
        position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: 8,
        overflow: "hidden", background: "#111", flexShrink: 0,
      }}>
        <img
          ref={bindImg}
          key={`${adKey}-${idx}-${list[idx]}`}
          src={currentSrc}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            opacity: loaded ? 1 : 0.15, transition: "opacity 0.15s ease",
          }}
        />
        {!loaded && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 18, opacity: 0.5, animation: "sp-spin 1s linear infinite", display: "inline-block" }}>⟳</span>
          </div>
        )}
        {list.length > 1 && (
          <>
            <button type="button" onClick={() => go((idx - 1 + list.length) % list.length)} style={{
              position: "absolute", left: 4, top: "50%", transform: "translateY(-50%)",
              padding: "2px 8px", background: "rgba(0,0,0,0.55)", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer", fontSize: 16, lineHeight: 1,
            }}>‹</button>
            <button type="button" onClick={() => go((idx + 1) % list.length)} style={{
              position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)",
              padding: "2px 8px", background: "rgba(0,0,0,0.55)", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer", fontSize: 16, lineHeight: 1,
            }}>›</button>
            <div style={{
              position: "absolute", bottom: 4, right: 4, background: "rgba(0,0,0,0.6)",
              color: "#fff", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
            }}>{idx + 1}/{list.length}</div>
          </>
        )}
      </div>
      {list.length > 1 && (
        <div style={{ display: "flex", gap: 3, overflowX: "auto", height: 36, flexShrink: 0 }}>
          {list.slice(0, 12).map((f, i) => (
            <button key={i} type="button" onClick={() => go(i)} style={{
              flexShrink: 0, width: 48, height: 34, borderRadius: 4, overflow: "hidden", padding: 0,
              border: `2px solid ${i === idx ? c.accentBlue : "transparent"}`,
              opacity: i === idx ? 1 : 0.55, cursor: "pointer", background: "#111",
            }}>
              <img src={proxyFoto(f)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { e.target.style.display = "none"; }} />
            </button>
          ))}
          {list.length > 12 && (
            <span style={{ fontSize: 9, color: c.textMuted, alignSelf: "center", padding: "0 4px" }}>+{list.length - 12}</span>
          )}
        </div>
      )}
    </div>
  );
}

function RevisionCard({ r, theme, onConfirm, onReject, onOpenAd, busy = false, puedeModificar = true }) {
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const esAdjuntar = r.tipo === "adjuntar" && (r.grupo_existente?.length > 0);
  const nuevos = esAdjuntar
    ? (r.anuncios || []).filter((a) => !a.id_propiedad_mercado)
    : (r.anuncios?.length >= 2 ? r.anuncios : [r.a, r.b].filter(Boolean));
  const grupoRef = esAdjuntar ? (r.grupo_existente || []) : [];
  const anuncios = esAdjuntar ? nuevos : (r.anuncios?.length >= 2 ? r.anuncios : [r.a, r.b].filter(Boolean));
  const minSel = esAdjuntar ? 1 : 2;

  const defaultSelected = () => {
    const sinVincular = anuncios.filter((a) => !a.id_propiedad_mercado);
    if (sinVincular.length >= minSel) return new Set(sinVincular.map((a) => a.id));
    return new Set(anuncios.map((a) => a.id));
  };

  const [selected, setSelected] = useState(defaultSelected);

  useEffect(() => {
    setSelected(defaultSelected());
  }, [r.id]);

  const toggleAd = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selCount = selected.size;

  const excerpt = (text, max = 180) => {
    const t = String(text || "").trim();
    if (!t) return null;
    return t.length > max ? `${t.slice(0, max)}…` : t;
  };

  const AdPanel = ({ ad, readOnly = false }) => {
    const desc = excerpt(ad.descripcion);
    const included = readOnly || selected.has(ad.id);
    return (
      <div style={{
        flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6,
        opacity: included ? 1 : 0.5,
        transition: "opacity 0.15s",
      }}>
        <RevisionFotoVisor fotos={ad.fotos} adKey={ad.id} theme={theme} />
        <div style={{
          padding: 8, borderRadius: 8,
          border: readOnly
            ? `2px solid ${isDark ? "rgba(255,255,255,0.15)" : c.border}`
            : included
              ? `2px solid ${c.accentBlue}`
              : `2px dashed ${isDark ? "rgba(255,255,255,0.25)" : c.border}`,
          background: readOnly
            ? (isDark ? "rgba(255,255,255,0.04)" : c.bgPanel)
            : included ? c.chipBg : (isDark ? "rgba(0,0,0,0.2)" : c.bgPanel),
          display: "flex", flexDirection: "column", gap: 4, flex: 1,
        }}>
          {!readOnly && (
            <label style={{
              display: "flex", alignItems: "center", gap: 6, cursor: puedeModificar ? "pointer" : "not-allowed",
              fontSize: 10, fontWeight: 700, color: included ? c.accentBlue : c.textMuted,
            }}>
              <input
                type="checkbox"
                checked={included}
                disabled={!puedeModificar}
                onChange={() => toggleAd(ad.id)}
                style={{ width: 14, height: 14, cursor: puedeModificar ? "pointer" : "not-allowed", accentColor: c.accentBlue }}
              />
              {ad.id_propiedad_mercado ? "Ya vinculado" : "Misma vivienda"}
            </label>
          )}
          {readOnly && (
            <span style={{ fontSize: 10, fontWeight: 700, color: c.textMuted }}>Grupo confirmado</span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <PortalLabel portal={ad.portal} />
            <button type="button" onClick={() => onOpenAd(ad)} style={{
              marginLeft: "auto", fontSize: 10, padding: "2px 8px", borderRadius: 4,
              border: isDark ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${c.border}`,
              background: isDark ? "rgba(255,255,255,0.12)" : c.bgPanel,
              color: isDark ? "#fff" : c.textPrimary,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
            }}>Ver ficha</button>
          </div>
          <p style={{ fontSize: 11, fontWeight: 600, color: c.textPrimary, margin: 0 }}>
            {ad.titulo || "Sin título"}
          </p>
          <p style={{ fontSize: 10, color: c.textMuted, margin: 0 }}>
            {[ad.precio_numerico && `${Number(ad.precio_numerico).toLocaleString("es-ES")}€`, ad.superficie_m2 && `${ad.superficie_m2} m²`, ad.habitaciones && `${ad.habitaciones} hab`].filter(Boolean).join(" · ")}
          </p>
          {desc ? (
            <p style={{
              fontSize: 10, color: c.textSecondary, margin: 0, lineHeight: 1.45,
              display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>{desc}</p>
          ) : (
            <p style={{ fontSize: 10, color: "#ef4444", margin: 0, fontWeight: 600 }}>⚠ Sin descripción</p>
          )}
        </div>
      </div>
    );
  };

  const marcoFoto = (r.reasons || []).some((x) => String(x).startsWith("marco_fotos"));

  return (
    <div style={{ border: `1px solid ${c.propCardBorder}`, borderRadius: 10, padding: 12, background: c.propCardBg }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.accentBlue }}>Score {r.score}</span>
        {esAdjuntar ? (
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff", background: "#059669",
            padding: "2px 8px", borderRadius: 6,
          }}>Anuncio nuevo → grupo ({r.grupo_existente_total ?? grupoRef.length} vinculados{r.grupo_existente_total > grupoRef.length ? `, muestra ${grupoRef.length}` : ""})</span>
        ) : (
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff", background: "#6366f1",
            padding: "2px 8px", borderRadius: 6,
          }}>{anuncios.length} portales{r.cluster_total && r.cluster_total > anuncios.length ? ` (muestra ${anuncios.length} de ${r.cluster_total})` : ""}</span>
        )}
        {marcoFoto && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff", background: "#7c3aed",
            padding: "2px 8px", borderRadius: 6,
          }}>Marco · fotos</span>
        )}
        <span style={{ fontSize: 10, color: c.textMuted }}>{(r.reasons || []).join(" · ")}</span>
      </div>
      {esAdjuntar ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "stretch" }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: c.textMuted, margin: "0 0 6px" }}>Grupo ya confirmado</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {grupoRef.map((ad) => <AdPanel key={ad.id} ad={ad} readOnly />)}
            </div>
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: c.accentBlue, margin: "0 0 6px" }}>Candidato nuevo</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {anuncios.map((ad) => <AdPanel key={ad.id} ad={ad} />)}
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(anuncios.length, 4)}, minmax(0, 1fr))`,
          gap: 10,
          alignItems: "stretch",
        }}>
          {anuncios.map((ad) => <AdPanel key={ad.id} ad={ad} />)}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button"
          disabled={busy || selCount < minSel || !puedeModificar}
          title={!puedeModificar ? "No tienes permiso para modificar la revisión" : undefined}
          onClick={() => onConfirm([...selected])}
          style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: busy || selCount < minSel || !puedeModificar ? "not-allowed" : "pointer",
            border: "none", background: selCount < minSel || busy || !puedeModificar ? c.border : c.accentGreen, color: "#fff", fontFamily: "inherit",
            opacity: selCount < minSel || busy || !puedeModificar ? 0.5 : 1,
          }}>{busy ? "Guardando…" : (esAdjuntar ? `✓ Vincular al grupo (${selCount})` : `✓ Vincular seleccionados (${selCount})`)}</button>
        <button type="button"
          disabled={busy || !puedeModificar}
          title={!puedeModificar ? "No tienes permiso para modificar la revisión" : undefined}
          onClick={onReject}
          style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: busy || !puedeModificar ? "not-allowed" : "pointer",
            border: `1px solid ${c.border}`, background: "transparent", color: c.textMuted, fontFamily: "inherit",
            opacity: busy || !puedeModificar ? 0.5 : 1,
          }}>✕ {esAdjuntar ? "No es la misma vivienda" : "Ninguno coincide"}</button>
        {selCount < minSel && (
          <span style={{ fontSize: 10, color: c.textMuted }}>
            {esAdjuntar ? "Confirma el anuncio nuevo" : "Marca al menos 2"}
          </span>
        )}
      </div>
    </div>
  );
}
